import { getPublicPath, getUserManager } from './utils';
import React, { FunctionComponent } from 'react';
import { UserManager, User } from 'oidc-client';
import { IUserAccess } from '@state/userAccess/types';
import { useHistory, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { IPublicSignInResponse } from '@state/auth/types';
import actions from '@state/actions';
import LoadingPage from '@components/LoadingPage';
import { Dispatch } from 'redux';
import { DesignPermissionEnum, IUserRequestAccess } from '@state/designs/types';
import { getUserAccessByEmail } from '@state/userAccess/utils';
import { GetUserByPrimaryDealer } from '@state/dealers/utils';
import { Dealer } from '@state/dealers/types';

const CryptoJS = require("crypto-js");

export const AuthContext = React.createContext(undefined as any);

export interface AuthProviderProps {
    onLoggedIn?: (user: any) => void
}

const signIn = (dispatch: Dispatch, userManager: UserManager, user: User) => {
    return new Promise((resolve, reject) => {
        dispatch(actions.auth.signIn(userManager, user, user.profile['dealeron.feature'] ?? [],  (result: any, error: any) => {
            if (error) reject(error)
            else resolve(result)
        }))
    })
}

const publicSignIn = (dispatch: Dispatch, userManager: UserManager, user: User | null, shareToken: string) => {
    return new Promise<IPublicSignInResponse>((resolve, reject) => {
        dispatch(actions.auth.publicSignIn(userManager, user, shareToken, (result: any, error: any) => {
            if (error) reject(error)
            else resolve(result)
        }))
    })
}

export const AuthProvider: FunctionComponent<any> = (props: React.PropsWithChildren<AuthProviderProps>) => {
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch()
    const [isLoading, setIsLoading] = React.useState(true);
    const [userData, setUserData] = React.useState<User | null>(null);
    const [userAccess, setUserAccess] = React.useState<IUserAccess | null>(null);
    const [dsPermissions, setDSPermissions] = React.useState<string[] | null>(null);
    const [loggedToken, setLoggedToken] = React.useState<Boolean>(false);
    const [userRequestAccess, setUserRequestAccess] = React.useState<IUserRequestAccess>({});
    const [userManager] = React.useState<UserManager>(getUserManager());
    const isMountedRef = React.useRef(true);
    const [LeadScienceUser, setLeadScienceUser] = React.useState<boolean>(false);

    const checkLoginStatus = async (user: User | null) => {
        if (user) signIn(dispatch, userManager, user);
    }

    const updatePath = (user: User | null) => {
        if (user?.state && user.state.pathname && user.state.pathname.length > 0) {
            history.push({pathname: `${user.state.pathname}${user.state.search ? user.state.search : ''}`});
        } else {
            history.push({pathname: `/studio`});
        }
    }

    const getLocalUser = (): User | null => {
        const encryptedUser = localStorage.getItem("user");
        if (encryptedUser) {
            const bytes = CryptoJS.AES.decrypt(encryptedUser, process.env.REACT_APP_DS_PUBLIC_USER_KEY);
            const localUser = bytes.toString(CryptoJS.enc.Utf8);
            return JSON.parse(localUser);
        }
        return null;
    }

  const isLeadScienceUser = async (curDealerId?: string) => {
    // curDealerId = 1 is ADMIN
    //if (!curDealerId || curDealerId === '' || curDealerId === '1') return false;
    if (!curDealerId || curDealerId === '') return false;
    const result:any = await GetUserByPrimaryDealer(dispatch,curDealerId);
    if(!result) return false;
    if((result as Dealer[]).length === 0) return false;
    let curDealer = (result as Dealer[])[0];
    return curDealer.IsLeadScienceClient;
  }    

  const pruneUser = (user: any|null) => {
    if (user && user.profile) {
        let tempOrganizations = user.profile["dealeron.dealerorganization"];
        if (typeof tempOrganizations === 'string') {
            //tempOrganizations = encodeURIComponent(tempOrganizations);
            tempOrganizations = tempOrganizations.replaceAll('&', 'n');    
        }
        user.profile["dealeron.dealerorganization"] = tempOrganizations;
    }
  }
    const updateUser = async (user: User | null) => {
        pruneUser(user);
        setUserData(user);
        const encryptedUser = CryptoJS.AES.encrypt(JSON.stringify(user), process.env.REACT_APP_DS_PUBLIC_USER_KEY).toString();
        localStorage.setItem("user", encryptedUser);
        if (user) {
            const response: any = await signIn(dispatch, userManager, user);
            setDSPermissions(response?.dsPermissions ?? [])
            if (user.profile.email) setUserAccess(await getUserAccessByEmail(dispatch, user.profile.email));
            if(user.profile?.['dealeron.primarydealer']) setLeadScienceUser(await isLeadScienceUser(user.profile?.['dealeron.primarydealer']));
            else setLeadScienceUser(false);
        }
    }

    React.useEffect(() => {
        const checkLogInStatus = async () => {
            const localUser = getLocalUser();
            const accessRequestInUrl = getAccessRequestInUrl(location);
            if (accessRequestInUrl) setUserRequestAccess(accessRequestInUrl);
            if (hasShareTokenInUrl(location)) {
                const searchParams = new URLSearchParams(location.search);
                const hashParams = new URLSearchParams(location.hash.replace('#', '?'));
                const shareToken = searchParams.get('share_token') ?? hashParams.get('share_token');
                const updatePreviews = searchParams.get('update_previews') ?? hashParams.get('update_previews');
                if (shareToken) {
                    try {
                        let user = localUser;
                        const publicUser = await publicSignIn(dispatch, userManager, user, shareToken);
                        if (!user) user = publicUser.metadata;
                        else checkLoginStatus(user);
                        pruneUser(user);
                        setUserData(user);
                        setLoggedToken(true);
                        history.push({pathname: getPublicPath(location, publicUser.publicAuth), search: (updatePreviews?"?update_previews=" + updatePreviews:"")});
                    } catch(error) {
                        history.push({pathname: `/studio`});
                        throw(error);
                    }
                }
            } else if (hasCodeInUrl(location)) {
                const user = await userManager.signinRedirectCallback();
                await updateUser(user);
                props.onLoggedIn && props.onLoggedIn(user)
                updatePath(user);
            } else if (!userData) {
                let user = await userManager?.getUser();                     
                if (!user && localUser) {
                    const userClass = new User(localUser as any);
                    user = userClass;
                    userManager?.storeUser(userClass);
                }
                if ((!user || user.expired)) {
                    localStorage.clear();
                    if (accessRequestInUrl.email) localStorage.setItem("requestAccessEmail", accessRequestInUrl.email ?? "");
                    if (accessRequestInUrl.permission) localStorage.setItem("requestAccessPermission", accessRequestInUrl.permission ?? "");
                    await userManager.signinRedirect({ state: location });
                } else if (isMountedRef.current) {
                    props.onLoggedIn && props.onLoggedIn(user)
                    await updateUser(user);
                    setIsLoading(false);
                }
            }
        }
        checkLogInStatus()
        if (location.pathname && userData) {
            setIsLoading(false);
        }
    }, [location]);

    React.useEffect(() => {
        const updateUserData = async () => {
            const user = await userManager.getUser();
            isMountedRef.current && setUserData(user);
            await updateUser(user);
        };
        userManager.events.addUserLoaded(updateUserData);
        return () => userManager.events.removeUserLoaded(updateUserData);
    }, [userManager]);

    return (
        <AuthContext.Provider value={{
            userManager,
            userData,
            userAccess,
            dsPermissions,
            loggedToken,
            userRequestAccess,
            setUserRequestAccess,
            setLoggedToken,
            isLoading,
            LeadScienceUser,
            logOut: async () => {
                localStorage.clear();
                setUserData(null);
                setDSPermissions(null);
                dispatch(actions.auth.setAuth(undefined, undefined, undefined))
                await userManager.signoutRedirect();
            },
        }}>
            {
                isLoading ?
                    <LoadingPage />
                    :
                    props.children
            }
        </AuthContext.Provider>
    )
}

export const hasCodeInUrl = (location: any): boolean => {
    const searchParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.replace('#', '?'));

    return Boolean(
        searchParams.get('code') ||
        searchParams.get('id_token') ||
        searchParams.get('session_state') ||
        hashParams.get('code') ||
        hashParams.get('id_token') ||
        hashParams.get('session_state'),
    );
};

export const hasShareTokenInUrl = (location: any): boolean => {
    const searchParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.replace('#', '?'));
    return Boolean(searchParams.get('share_token') || hashParams.get('share_token'));
};

export const getAccessRequestInUrl = (location: any) => {
    const searchParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.replace('#', '?'));
    const permission = searchParams.get('permission') ?? hashParams.get('permission') ?? localStorage.getItem("requestAccessPermission");
    return {
        email: searchParams.get('email_access_request') ?? hashParams.get('email_access_request') ?? localStorage.getItem("requestAccessEmail"),
        permission: permission ? permission as DesignPermissionEnum : null,
    };
};